/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Symmetry is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Symmetry is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Symmetry, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Symmetry:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Leverages 'code splitting' for some features.
 *  - Is completely custom built (no JS libraries)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Symmetry broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
*   4.  on:cart:after-merge
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - allVariants: an array of all variants
 *  - selectedOptions: an array of currently selected options (e.g. ['Blue', 'Large'])
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:after-merge
 * -------------------------------------------------------------------------------------------------
 * Fires after a list of cart items has finished being dynamically updated after a cart change.
 *
 * How to listen:
 * document.addEventListener('on:cart:after-merge', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Symmetry utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Symmetry has no third-party JavaScript dependencies.
 *
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */






class SubDrawerDialogOpener extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button, input:checked');
    if (this.button) {
      this.bindEvents();
    }
  }

  bindEvents() {
    this.button.addEventListener('click', (evt) => {
      evt.preventDefault();
      const drawer = this.closest('.drawer');
      const subDrawerDialog = drawer.querySelector(this.getAttribute('data-id'));
      if (subDrawerDialog && !subDrawerDialog.isOpen) {
        subDrawerDialog.open();
      }
    });
  }
}

customElements.define('sub-drawer-dialog-opener', SubDrawerDialogOpener);

class SubDrawerDialog extends HTMLElement {
  connectedCallback() {
    this.isOpen = false;
    this.drawer = this.closest('.drawer');
    this.title = this.querySelector('[data-subdrawer-title]');
    this.sectionRerender = this.querySelector('section-renderer');
    this.drawerInnerOverlay = this.drawer.querySelector('[drawer-inner-overlay]');
    this.closeButtons = this.querySelectorAll('.js-close-sub-drawer');
    if (this.closeButtons) {
      this.bindEvents();
    }

    // Listen for main drawer close events
    this.bindDrawerCloseEvents();
  }

  bindEvents() {
    this.closeButtons.forEach(button => {
      button.addEventListener('click', (evt) => {
        evt.preventDefault();
        this.close();
      });
    });

    this.drawer.addEventListener('click', (evt) => {
      if (this.isOpen && !this.contains(evt.target) && !evt.target.closest('.subdrawer-dialog')) {
        if (!evt.target.closest('[data-id]')) {
          this.close();
        }
      }
    });

    // Close when clicking outside the main drawer (on the overlay)
    const overlay = document.querySelector('.js-overlay');
    if (overlay) {
      overlay.addEventListener('click', (evt) => {
        if (this.isOpen && evt.target === overlay) {
          this.close();
        }
      });
    }
  }

  bindDrawerCloseEvents() {
    // Listen for the main drawer's after-close event
    if (this.drawer) {
      const drawerName = this.drawer.dataset.name;
      if (drawerName) {
        document.addEventListener(`on:${drawerName}:after-close`, () => {
          if (this.isOpen) {
            this.close();
          }
        });
      }
    }
  }

  toggleDrawerInnerOverlay(isOpen) {
    if (isOpen) {
      this.drawerInnerOverlay.setAttribute('aria-hidden', 'false');
    } else {
      this.drawerInnerOverlay.setAttribute('aria-hidden', 'true');
    }
  }

  open(newTitle, sectionRerenderName) {
    this.isOpen = true;
    if (newTitle) {
      this.title.textContent = newTitle;
    }
    if (sectionRerenderName) {
      this.sectionRerender.setAttribute('data-section-name', sectionRerenderName);
    }
    this.toggleDrawerInnerOverlay(true);
    this.setAttribute('aria-hidden', 'false');
  }

  close() {
    this.isOpen = false;
    this.toggleDrawerInnerOverlay(false);
    this.setAttribute('aria-hidden', 'true');
  }
}

customElements.define('sub-drawer-dialog', SubDrawerDialog);

class SiteTabs extends HTMLElement {
  constructor() {
    super();
    this.activeTab = null;
    this.activeTabContent = null;
    this.progressBar = this.querySelector('#lens-selection-progress-bar');
    this.shouldScroll = this.hasAttribute('scroll-to-content');
  }

  connectedCallback() {
    this.initTabs();
    this.attachEvents();
    // Initialize progress bar
    this.updateProgressBar();
  }

  getProgress() {
    const currentIndex = this.getCurrentTabIndex();
    const totalTabs = this.getTotalTabs();
    return totalTabs > 0 ? (currentIndex + 1) / totalTabs * 100 : 0;
  }

  updateProgressBar() {
    if (this.progressBar) {
      this.progressBar.setAttribute('value', this.getProgress());
    }
  }

  initTabs() {
    this.tabTriggers = [...this.querySelectorAll('[tab-trigger]')];
    this.tabContents = [...this.querySelectorAll('[tab-content]')];

    // If no visible triggers, create virtual triggers for navigation
    if (this.tabTriggers.length === 0 && this.tabContents.length > 0) {
      this.tabTriggers = this.tabContents.map((content, index) => {
        const virtualTrigger = document.createElement('button');
        const tabContentValue = content.getAttribute('tab-content');
        virtualTrigger.setAttribute('tab-trigger', tabContentValue);
        virtualTrigger.style.display = 'none'; // Hide virtual triggers
        this.appendChild(virtualTrigger);
        return virtualTrigger;
      });
    }

    const initiallyActiveTab = this.tabTriggers.find((tab) =>
      tab.hasAttribute('active')
    );
    if (initiallyActiveTab) {
      this.activeTab = initiallyActiveTab;
    } else {
      this.activeTab = this.tabTriggers.length ? this.tabTriggers[0] : null;
    }

    this.activeTabContent = this.getTabContent(this.activeTab);
  }

  attachEvents() {
    const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    const enableHover = this.hasAttribute('desktop-hover') && !isTouchDevice;

    // Only attach click events to visible triggers
    this.tabTriggers.forEach((trigger) => {
      if (trigger.style.display !== 'none') {
        if (enableHover) {
          trigger.addEventListener('mouseenter', () => this.handleTabClick(trigger));
        } else {
          trigger.addEventListener('click', () => this.handleTabClick(trigger));
        }
      }
    });


    // Update progress bar on tab changes
    this.addEventListener('on:tab:change', () => {
      this.updateProgressBar();
    });

    // Add click events for custom tab triggers
    this.querySelectorAll('[data-tab-trigger]').forEach((trigger) => {
      trigger.addEventListener('click', (event) => {
        event.preventDefault();
        const targetTab = trigger.getAttribute('data-tab-trigger');
        const targetTabElement = this.tabTriggers.find(tab => tab.getAttribute('tab-trigger') === targetTab);
        if (targetTabElement) {
          this.setActiveTab(targetTabElement);
          this.dispatchEvent(new CustomEvent('on:tab:change', {
            detail: {
              tab: targetTabElement,
              isFirstTab: targetTabElement === this.tabTriggers[0],
              isLastTab: targetTabElement === this.tabTriggers[this.tabTriggers.length - 1]
            }
          }));
        }
      });
    });

    // Add navigation button event listeners
    this.attachNavigationEvents();
  }

  attachNavigationEvents() {
    const prevButtons = this.querySelectorAll('[tab-nav-prev]');
    const nextButtons = this.querySelectorAll('[tab-nav-next]');

    prevButtons.forEach(button => {
      button.addEventListener('click', () => this.navigateToPrevious());
    });

    nextButtons.forEach(button => {
      button.addEventListener('click', () => this.navigateToNext());
    });
  }

  navigateToPrevious() {
    if (!this.activeTab || this.tabTriggers.length <= 1) return;

    const currentIndex = this.tabTriggers.indexOf(this.activeTab);

    // Don't navigate if we're already at the first tab
    if (currentIndex <= 0) return;

    const previousIndex = currentIndex - 1;
    const previousTab = this.tabTriggers[previousIndex];

    this.setActiveTab(previousTab);
  }

  navigateToNext() {
    if (!this.activeTab || this.tabTriggers.length <= 1) return;

    const currentIndex = this.tabTriggers.indexOf(this.activeTab);

    // Don't navigate if we're already at the last tab
    if (currentIndex >= this.tabTriggers.length - 1) return;

    const nextIndex = currentIndex + 1;
    const nextTab = this.tabTriggers[nextIndex];

    this.setActiveTab(nextTab);
  }

  getCurrentTabIndex() {
    return this.activeTab ? this.tabTriggers.indexOf(this.activeTab) : -1;
  }

  getTotalTabs() {
    return this.tabTriggers.length;
  }

  handleTabClick(newTab) {
    if (newTab === this.activeTab) return;
    this.setActiveTab(newTab);
  }

  setActiveTab(tab) {
    if (!tab) return;
    this.clearActiveStates();
    this.activeTab = tab;
    this.activeTabContent = this.getTabContent(tab);

    if (this.activeTab && this.activeTabContent) {
      this.activeTab.setAttribute('active', '');
      this.activeTabContent.setAttribute('open', '');
      this.updateWrapperState(tab, true);
      this.scrollToContent();
      this.updateNavigationState();

      // Update progress bar and dispatch event
      this.updateProgressBar();
      this.dispatchEvent(new CustomEvent('on:tab:change', {
        detail: {
          tab: tab,
          isFirstTab: tab === this.tabTriggers[0],
          isLastTab: tab === this.tabTriggers[this.tabTriggers.length - 1]
        }
      }));
    }
  }

  updateNavigationState() {
    const prevButtons = this.querySelectorAll('[tab-nav-prev]');
    const nextButtons = this.querySelectorAll('[tab-nav-next]');
    const currentIndex = this.getCurrentTabIndex();
    const totalTabs = this.getTotalTabs();

    // Update all previous buttons
    prevButtons.forEach(button => {
      const isFirstTab = currentIndex <= 0;
      button.disabled = totalTabs <= 1 || isFirstTab;
      button.setAttribute('aria-label', `Previous tab (${currentIndex + 1} of ${totalTabs})`);
    });

    // Update all next buttons
    nextButtons.forEach(button => {
      const isLastTab = currentIndex >= totalTabs - 1;
      button.disabled = totalTabs <= 1 || isLastTab;
      button.setAttribute('aria-label', `Next tab (${currentIndex + 1} of ${totalTabs})`);
    });
  }

  clearActiveStates() {
    this.tabTriggers.forEach((tab) => {
      tab.removeAttribute('active');
      this.updateWrapperState(tab, false);
    });
    this.tabContents.forEach((content) => {
      content.removeAttribute('open');
    });
  }

  getTabContent(tab) {
    return this.querySelector(
      `[tab-content="${tab.getAttribute('tab-trigger')}"]`
    );
  }

  updateWrapperState(tab, isActive) {
    const wrapper = tab.closest('[tab-trigger-wrapper]');
    if (wrapper) {
      wrapper.toggleAttribute('tab-trigger-active', isActive);
    }
  }

  scrollToContent() {
    if (this.shouldScroll && this.activeTabContent) {
      this.activeTabContent.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }
}

customElements.define('site-tabs', SiteTabs);

class ProgressBar extends HTMLElement {
  static get observedAttributes() {
    return ['value'];
  }

  constructor() {
    super();
    const bar = document.createElement('div');
    bar.className = 'bar';
    this.appendChild(bar);
    this._bar = bar;
  }

  attributeChangedCallback(name, _, newVal) {
    if (name === 'value') {
      this.setProgress(parseFloat(newVal));
    }
  }

  setProgress(percent) {
    const clamped = Math.min(100, Math.max(0, percent));
    this._bar.style.width = `${clamped}%`;
  }
}

customElements.define('progress-bar', ProgressBar);

class ProductFormUpdater extends HTMLElement {
  constructor() {
    super();
    this.isMiniForm = false;
    this.submitButton = this.querySelector('button[type="submit"]');
    this.mainForm = document.querySelector(`#${this.getAttribute('data-main-form')}`);

    this.autoSubmit = this.hasAttribute('data-auto-submit');
  }

  onSubmit(event) {
    event.preventDefault();
    this.updateMainForm();
  }

  updateMainForm() {
    // Get all form inputs from this form
    const formData = new FormData(this);

    // Update inputs in the main form using data-target-name attributes
    for (let [name, value] of formData.entries()) {
      // Find the input in this form that has this name
      const sourceInput = this.querySelector(`input[name="${name}"]`);

      if (sourceInput && sourceInput.hasAttribute('data-target-name')) {
        // Get the target name from data-target-name attribute
        const targetName = sourceInput.getAttribute('data-target-name');

        // Find or create the target input in the main form
        let targetInput = this.mainForm.querySelector(`input[name="${targetName}"]`);

        if (!targetInput) {
          // Create new input if it doesn't exist
          targetInput = document.createElement('input');
          targetInput.type = 'hidden';
          targetInput.name = targetName;
          this.mainForm.appendChild(targetInput);
        }

        // Update the target input value
        targetInput.value = value;
      }
    }
  }

  // Handle radio button changes for auto-submit
  handleRadioChange(event) {
    if (this.autoSubmit && event.target.type === 'radio') {
      // Small delay to ensure the radio is properly selected
      setTimeout(() => {
        this.onSubmit(new Event('submit'));
      }, 100);
    }
  }

  connectedCallback() {
    this.addEventListener('submit', this.onSubmit.bind(this));

    // Add listeners for radio buttons if auto-submit is enabled
    if (this.autoSubmit) {
      const radioButtons = this.querySelectorAll('input[type="radio"]');
      radioButtons.forEach(radio => {
        radio.addEventListener('change', this.handleRadioChange.bind(this));
      });
    }
  }
}

customElements.define('product-form-updater', ProductFormUpdater);


// class SectionRenderer extends HTMLElement {
//   constructor() {
//     super();
//   }

//   async render() {
//     const sectionName = this.getAttribute('data-section-name');
//     const sectionTargetContainerId = this.getAttribute('data-section-target-container-id');
//     const sectionProductHandle = this.getAttribute('data-section-product-handle');
//     const sectionVariantId = this.getAttribute('data-section-variant-id');
//     const sectionTargetContainer = this.querySelector('[data-container-to-attach]');

//     // Validate required attributes
//     if (!sectionName) {
//       console.error('[SectionRenderer] Missing required attribute: data-section-name');
//       return;
//     }

//     if (!sectionTargetContainer) {
//       console.error('[SectionRenderer] Could not find container with data-container-to-attach attribute');
//       return;
//     }

//     let url = `${Shopify.routes.root}search?section_id=${sectionName}&product_handle=${sectionProductHandle}`;
//     if (sectionVariantId) {
//       url += `&variant_id=${sectionVariantId}`;
//     }
//     try {
//       const res = await fetch(url);

//       if (!res.ok) {
//         throw new Error(`HTTP error! status: ${res.status}`);
//       }

//       const html = await res.text();

//       const template = document.createElement('template');
//       template.innerHTML = html;

//       const sectionSelector = `[id^="shopify-section-${sectionName}"]`;
//       const sectionRoot = template.content.querySelector(sectionSelector);

//       if (!sectionRoot) {
//         console.warn('[SectionRenderer] Could not find section root in fetched HTML');
//         return;
//       }

//       const newHTML = sectionRoot.innerHTML.trim();
//       const currentHTML = sectionTargetContainer.innerHTML.trim();

//       if (newHTML === currentHTML) {
//         return;
//       }

//       sectionTargetContainer.replaceChildren(...sectionRoot.children);
//     } catch (error) {
//       console.error('[SectionRenderer] Error fetching section content:', error);
//     }
//   }
// }

// customElements.define('section-renderer', SectionRenderer);

class SectionRenderer extends HTMLElement {
  static cache = new Map();
  constructor() { super(); this._abort = null; }

  connectedCallback() { if (this.hasAttribute('auto')) this.render(); }
  disconnectedCallback() { if (this._abort) this._abort.abort(); }

  async render() {
    const name = this.getAttribute('data-section-name');
    const handle = this.getAttribute('data-section-product-handle') || '';
    const variantId = this.getAttribute('data-section-variant-id') || '';
    const targetId = this.getAttribute('data-section-target-container-id');
    const container = this.querySelector('[data-container-to-attach]') || (targetId && document.getElementById(targetId));
    if (!name) return console.error('[SectionRenderer] Missing data-section-name');
    if (!container) return console.error('[SectionRenderer] Missing target container');

    if (this._abort) this._abort.abort();
    this._abort = new AbortController();

    const url = this._buildURL({ name, handle, variantId });
    this.toggleAttribute('loading', true);
    this.dispatchEvent(new CustomEvent('section:before-render', { bubbles: true, detail: { url, name, container } }));

    try {
      const html = await this._getHTML(url, this._abort.signal);
      this._swap(container, html, name);
      this.dispatchEvent(new CustomEvent('section:rendered', { bubbles: true, detail: { name, container } }));
    } catch (e) {
      if (e.name !== 'AbortError') {
        console.error('[SectionRenderer] fetch/render error', e);
        this.dispatchEvent(new CustomEvent('section:error', { bubbles: true, detail: { error: e, url, name } }));
      }
    } finally {
      this.toggleAttribute('loading', false);
      this._abort = null;
    }
  }

  _buildURL({ name, handle, variantId }) {
    const base = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    const params = new URLSearchParams({ section_id: name });
    if (handle) params.set('product_handle', handle);
    if (variantId) params.set('variant_id', variantId);

    // merge custom params (query string OR JSON in data-params, plus data-param-*)
    const extras = this._collectCustomParams();
    for (const [k, v] of extras) {
      params.delete(k);
      (Array.isArray(v) ? v : [v]).forEach(val => params.append(k, String(val)));
    }
    return `${base}search?${params.toString()}`;
  }

  _collectCustomParams() {
    const out = new Map();

    // data-params="a=1&b=2"  OR  data-params='{"a":1,"b":["x","y"]}'
    const attr = this.getAttribute('data-params');
    if (attr) {
      if (attr.trim().startsWith('{')) {
        try {
          const obj = JSON.parse(attr);
          Object.entries(obj).forEach(([k, v]) => out.set(k, v));
        } catch (e) {
          console.warn('[SectionRenderer] Invalid JSON in data-params', e);
        }
      } else {
        const qs = new URLSearchParams(attr);
        for (const [k, v] of qs) {
          if (out.has(k)) {
            const prev = out.get(k);
            out.set(k, Array.isArray(prev) ? [...prev, v] : [prev, v]);
          } else {
            out.set(k, v);
          }
        }
      }
    }

    // data-param-* → key is the suffix; allow __ → - if you want dashes
    for (const { name, value } of Array.from(this.attributes)) {
      if (name.startsWith('data-param-')) {
        const key = name.replace(/^data-param-/, '').replace(/__/g, '-');
        out.set(key, value);
      }
    }

    return out;
  }

  async _getHTML(url, signal) {
    if (!this.hasAttribute('no-cache') && SectionRenderer.cache.has(url)) {
      return SectionRenderer.cache.get(url);
    }
    const res = await fetch(url, { credentials: 'same-origin', signal });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const html = await res.text();
    if (!this.hasAttribute('no-cache')) SectionRenderer.cache.set(url, html);
    return html;
  }

  _swap(container, html, sectionName) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const esc = (window.CSS && CSS.escape) ? CSS.escape(sectionName) : sectionName.replace(/[^a-zA-Z0-9_-]/g, '');
    const root = doc.querySelector(`[id^="shopify-section-${esc}"]`);
    if (!root) return console.warn('[SectionRenderer] Section root not found in response');

    const frag = document.createDocumentFragment();
    Array.from(root.children).forEach(n => frag.appendChild(document.importNode(n, true)));
    container.replaceChildren(frag);

    // re-exec inline scripts
    container.querySelectorAll('script').forEach(old => {
      const s = document.createElement('script');
      for (const { name, value } of old.attributes) s.setAttribute(name, value);
      if (!old.src) s.textContent = old.textContent || '';
      old.replaceWith(s);
    });
  }
}

customElements.define('section-renderer', SectionRenderer);

class FloatingElementsHandler {
  constructor() {
    this.EVENT = 'site:floating-elements';
    this.onEvent = this.onEvent.bind(this);
  }

  onEvent(e) {
    const html = document.body;
    const { hide, toggle } = e.detail ?? {};

    if (typeof hide === 'boolean') {
      html.toggleAttribute('floating-elements-hidden', hide);
      return;
    }

    if (toggle) {
      html.toggleAttribute('floating-elements-hidden');
      return;
    }

    const currentState = html.hasAttribute('floating-elements-hidden');
    html.toggleAttribute('floating-elements-hidden', !currentState);
  }

  init() { window.addEventListener(this.EVENT, this.onEvent); }
  destroy() { window.removeEventListener(this.EVENT, this.onEvent); }
  show() { window.dispatchEvent(new CustomEvent(this.EVENT, { detail: { hide: false } })); }
  hide() { window.dispatchEvent(new CustomEvent(this.EVENT, { detail: { hide: true } })); }
  toggle() { window.dispatchEvent(new CustomEvent(this.EVENT, { detail: { toggle: true } })); }
}
const floatingElementsHandler = new FloatingElementsHandler();
floatingElementsHandler.init();



class LayeredButtonStack extends HTMLElement {
  static get observedAttributes() { return ['active-index', 'gap']; }

  constructor() {
    super();
    this._idx = 0;
    this._buttons = [];
    this._ro = null;

    // simple viewport + track
    this._viewport = document.createElement('div');
    this._viewport.className = 'layered-button-stack__viewport';
    this._track = document.createElement('div');
    this._track.className = 'layered-button-stack__track';
    this._track.style.display = 'flex';
    this._track.style.flexDirection = 'column';
    this._track.style.willChange = 'transform';

    this._viewport.appendChild(this._track);
  }

  connectedCallback() {
    // move current children into track
    const kids = Array.from(this.children);
    this.appendChild(this._viewport);
    kids.forEach(k => this._track.appendChild(k));

    // apply initial gap (attribute or 0)
    this._applyGap();

    // collect buttons + index them
    this._buttons = Array.from(this._track.children);
    this._buttons.forEach((btn, i) => {
      btn.dataset.buttonIndex ??= String(i);
      btn.classList.add('layered-button-stack__button');
    });

    // initial layout
    this._layout(false);

    // click to activate
    this.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-button-index]');
      if (!btn) return;
      this.showButton(+btn.dataset.buttonIndex);
    });

    // keyboard nav
    this.tabIndex ||= 0;
    this.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowDown') { e.preventDefault(); this.next(); }
      if (e.key === 'ArrowUp') { e.preventDefault(); this.prev(); }
    });

    // keep height/step in sync
    this._ro = new ResizeObserver(() => this._layout());
    this._ro.observe(this);
  }

  disconnectedCallback() { this._ro?.disconnect(); }

  attributeChangedCallback(name, _old, val) {
    if (name === 'active-index') {
      const i = Number(val);
      if (!Number.isNaN(i)) this._setIndex(i);
    }
    if (name === 'gap') {
      this._applyGap();
      this._layout(); // recalc step with new gap
    }
  }

  // Public API
  showButton(i) { this.setAttribute('active-index', String(i)); }
  next() { if (this._buttons.length) this.showButton((this._idx + 1) % this._buttons.length); }
  prev() { if (this._buttons.length) this.showButton((this._idx - 1 + this._buttons.length) % this._buttons.length); }

  // Internals
  _applyGap() {
    const gapAttr = this.getAttribute('gap');
    this._track.style.gap = gapAttr ?? '0';
  }

  _gapPx() {
    // read resolved row gap in px (handles px/rem/etc.)
    const cs = getComputedStyle(this._track);
    return parseFloat(cs.rowGap) || 0;
  }

  _layout(animate = true) {
    const first = this._buttons[0];
    if (!first) return;

    const h = first.getBoundingClientRect().height;
    const step = h + this._gapPx();

    // lock viewport to one button height
    this._viewport.style.overflow = 'hidden';
    this._viewport.style.height = h + 'px';

    if (!animate) this._track.style.transition = 'none';
    this._track.style.transform = `translateY(${-this._idx * step}px)`;
    if (!animate) {
      // force reflow then restore transition to allow future anims
      this._track.getBoundingClientRect();
      this._track.style.removeProperty('transition');
    }

    this._updateA11y();
    this._applyCascadeDelays();
  }

  _setIndex(i) {
    const max = this._buttons.length - 1;
    const clamped = Math.max(0, Math.min(i, max));
    if (clamped === this._idx) return;
    this._idx = clamped;

    this._buttons.forEach((btn, j) =>
      btn.classList.toggle('layered-button-stack__button--active', j === clamped)
    );

    this._layout(true);

    this.dispatchEvent(new CustomEvent('layered-button:change', {
      bubbles: true,
      detail: { index: clamped, button: this._buttons[clamped] }
    }));
  }

  _updateA11y() {
    this._buttons.forEach((btn, i) => {
      const active = i === this._idx;
      btn.setAttribute('aria-hidden', String(!active));
      // inert if supported
      try { btn.inert = !active; } catch { }
      if (!active) btn.setAttribute('tabindex', '-1'); else btn.removeAttribute('tabindex');
    });
  }

  _applyCascadeDelays() {
    const baseDelay = 50; // ms per index
    this._buttons.forEach((btn, i) => {
      const offset = Math.abs(i - this._idx);
      btn.style.transitionDelay = `${offset * baseDelay}ms`;
    });
  }
}

customElements.define('layered-button-stack', LayeredButtonStack);


class NativeSelect extends HTMLElement {
  constructor() {
    super();
    this.select = this.querySelector('select');
    this.onChange = this.onChange.bind(this);
  }

  connectedCallback() {
    if (!this.select) {
      this.select = this.querySelector('select');
      if (!this.select) return;
    }

    this.select.addEventListener('change', this.onChange);
    this.select.addEventListener('input', this.onChange);
    this.select.addEventListener('blur', () => this.validate());

    this.validate();
  }

  disconnectedCallback() {
    if (!this.select) return;
    this.select.removeEventListener('change', this.onChange);
    this.select.removeEventListener('input', this.onChange);
  }

  onChange() {
    this.validate();
    this.dispatchEvent(new CustomEvent('change', {
      detail: { value: this.select.value },
      bubbles: true,
      composed: true
    }));
  }

  validate() {
    if (!this.select) return false;

    if (this.select.disabled) {
      this.removeAttribute('valid-select');
      this.classList.remove('input-success', 'input-error');
      this.select.setCustomValidity('');
      return true;
    }

    const idx = this.select.selectedIndex;
    const opt = idx >= 0 ? this.select.options[idx] : null;
    const disabledSelected = !!opt && opt.disabled;

    let valid = this.select.validity.valid;

    if (disabledSelected) {
      this.select.setCustomValidity('Please select an available option.');
      valid = false;
    } else {
      this.select.setCustomValidity('');
    }

    // Only remove classes, never add them automatically
    if (!valid) {
      this.classList.remove('input-success');
    } else {
      this.classList.remove('input-error');
    }

    // Update attribute
    this.setAttribute('valid-select', String(valid));

    return valid;
  }
}

customElements.define('native-select', NativeSelect);


class NotificationCenter {
  static container = null;
  static maxVisible = 3

  static mount(target) {
    this.container = this._getContainer(target || document.body);
  }

  static init() {
    if (!this.container) this.mount(document.body);
    window.addEventListener("notification-center:notify", e => {
      const { message, options = {} } = e.detail || {};
      this.show(message, options);
    });
    window.addEventListener("keydown", e => {
      if (e.key === "Escape") {
        this.container?.lastElementChild?.querySelector(".notification__close")?.click();
      }
    });
  }

  static notify(message, options = {}) {
    window.dispatchEvent(new CustomEvent("notification-center:notify", {
      detail: { message, options }
    }));
  }

  static show(message, { type = "default", duration = 2200, important = false, container } = {}) {
    const root = container ? this._getContainer(container) : (this.container || this._getContainer(document.body));

    root.setAttribute("aria-live", (type === "error" || important) ? "assertive" : "polite");
    while (root.children.length >= this.maxVisible) root.firstElementChild?.remove();

    const el = document.createElement("div");
    el.className = `notification notification--${type}`;
    el.setAttribute("role", "status");

    const span = document.createElement("span");
    span.className = "notification__message";
    span.textContent = String(message ?? "");

    const btn = document.createElement("button");
    btn.className = "notification__close";
    btn.type = "button";
    btn.setAttribute("aria-label", "Close");
    btn.textContent = "×";

    el.append(span, btn);
    root.appendChild(el);

    el.animate([{ opacity: 0, transform: "translateY(-8px)" }, { opacity: 1, transform: "translateY(0)" }],
      { duration: 180, easing: "cubic-bezier(.2,.8,.2,1)", fill: "forwards" });

    let removed = false, timer;
    const clear = () => { if (timer) clearTimeout(timer); };
    const schedule = () => { if (duration !== Infinity) timer = setTimeout(remove, duration); };
    const remove = () => {
      if (removed) return;
      removed = true; clear();
      el.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 160 }).finished.finally(() => el.remove());
    };

    btn.addEventListener("click", remove);
    el.addEventListener("mouseenter", clear);
    el.addEventListener("mouseleave", schedule);
    el.addEventListener("focusin", clear);
    el.addEventListener("focusout", schedule);
    schedule();
  }

  static _getContainer(target) {
    const root = typeof target === "string" ? document.querySelector(target) : target;
    let c = root?.querySelector?.(".notification-center");
    if (!c) {
      c = document.createElement("div");
      c.className = "notification-center";
      c.setAttribute("aria-live", "polite");
      c.setAttribute("aria-atomic", "false");
      c.role = "region";
      root?.appendChild(c);
    }
    return c;
  }
}

NotificationCenter.init();


class FollowCursorTooltip {
  constructor(selector) {
    this.tooltip = document.createElement("div");
    this.tooltip.className = "tooltip";
    document.body.appendChild(this.tooltip);

    document.querySelectorAll(selector).forEach(el => {
      el.addEventListener("mouseenter", e => this.show(e, el));
      el.addEventListener("mousemove", e => this.move(e));
      el.addEventListener("mouseleave", () => this.hide());
    });
  }

  show(event, el) {
    this.tooltip.textContent = el.getAttribute("data-tooltip") || "Tooltip";
    this.tooltip.style.opacity = "1";
    this.move(event); // set initial position
  }

  move(event) {
    const offset = 12; // px away from cursor
    this.tooltip.style.left = event.clientX + offset + "px";
    this.tooltip.style.top = event.clientY + offset + "px";
  }

  hide() {
    this.tooltip.style.opacity = "0";
  }
}

new FollowCursorTooltip("[data-tooltip]");

// ============================================================================
// FORM RESETTER COMPONENT
// ============================================================================

class FormResetter extends HTMLElement {
  constructor() {
    super();
    this.resetButton = null;
  }

  connectedCallback() {
    this.resetButton = this.querySelector('button, [data-reset-trigger]');
    if (this.resetButton) {
      this.resetButton.addEventListener('click', (e) => {
        e.preventDefault();
        this.resetForm();
      });
    }
  }

  resetForm() {
    const form = this.findTargetForm();
    if (!form) {
      console.warn('FormResetter: No form found to reset');
      return;
    }

    // Reset all form elements
    this.resetFormElements(form);

    // Trigger custom event
    this.dispatchEvent(new CustomEvent('form:reset', {
      detail: { form: form },
      bubbles: true
    }));

    // Show notification if enabled
    if (this.hasAttribute('data-show-notification')) {
      const message = this.getAttribute('data-notification-message') || 'Form has been reset';
      NotificationCenter.show(message, { type: 'success' });
    }
  }

  findTargetForm() {
    // First try to find the closest form element
    let form = this.closest('form');

    // If no form found, look for data-js-form
    if (!form) {
      form = this.closest('[data-js-form]');
    }

    // If still no form found, look for any form in the same container
    if (!form) {
      const container = this.closest('[data-form-container], .form-container, .form-wrapper') || this.parentElement;
      form = container?.querySelector('form, [data-js-form]');
    }

    return form;
  }

  resetFormElements(form) {
    // Reset all input elements
    form.querySelectorAll('input').forEach(input => {
      switch (input.type) {
        case 'checkbox':
          input.checked = false;
          break;
        case 'radio':
          input.checked = false;
          break;
        case 'file':
          input.value = '';
          break;
        case 'hidden':
          // Don't reset hidden fields by default
          if (!input.hasAttribute('data-preserve-hidden')) {
            input.value = '';
          }
          break;
        default:
          input.value = '';
          break;
      }

      // Remove validation classes
      input.classList.remove('input-success', 'input-warning', 'input-error');

      // Trigger change event
      input.dispatchEvent(new Event('change', { bubbles: true }));
      input.dispatchEvent(new Event('input', { bubbles: true }));
    });

    // Reset all select elements
    form.querySelectorAll('select').forEach(select => {
      // Reset to first option or default value
      if (select.options.length > 0) {
        select.selectedIndex = 0;
        select.value = select.options[0].value;
      }

      // Remove validation classes
      select.classList.remove('input-success', 'input-warning', 'input-error');

      // Trigger change event
      select.dispatchEvent(new Event('change', { bubbles: true }));
    });

    // Reset all textarea elements
    form.querySelectorAll('textarea').forEach(textarea => {
      textarea.value = '';

      // Remove validation classes
      textarea.classList.remove('input-success', 'input-warning', 'input-error');

      // Trigger change event
      textarea.dispatchEvent(new Event('change', { bubbles: true }));
      textarea.dispatchEvent(new Event('input', { bubbles: true }));
    });

    // Reset custom select components (if they exist)
    form.querySelectorAll('custom-select, native-select').forEach(customSelect => {
      if (customSelect.reset) {
        customSelect.reset();
      } else {
        // Fallback: try to reset the underlying select
        const underlyingSelect = customSelect.querySelector('select');
        if (underlyingSelect) {
          underlyingSelect.selectedIndex = 0;
          underlyingSelect.value = underlyingSelect.options[0]?.value || '';
          underlyingSelect.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    });

    // Reset prescription form components (if they exist)
    form.querySelectorAll('prescription-form').forEach(prescriptionForm => {
      if (prescriptionForm.loadData) {
        prescriptionForm.loadData();
      }
    });

    // Reset variant picker components (if they exist)
    form.querySelectorAll('variant-picker').forEach(variantPicker => {
      if (variantPicker.reset) {
        variantPicker.reset();
      }
    });

    // Clear any validation messages
    form.querySelectorAll('.validation-messages, .validation-message').forEach(message => {
      message.remove();
    });

    // Reset any custom form components that have a reset method
    form.querySelectorAll('*').forEach(element => {
      if (element.reset && typeof element.reset === 'function') {
        try {
          element.reset();
        } catch (error) {
          console.warn('Error resetting component:', element, error);
        }
      }
    });

    // Trigger form reset event
    form.dispatchEvent(new Event('form:reset', { bubbles: true }));
  }
}

// Register the custom element
customElements.define('form-resetter', FormResetter);